import { CustomerIcon } from '@/assets/icons/customer-icon';
import { OrderIcon } from '@/assets/icons/order-icon';
import { ProductIcon } from '@/assets/icons/product-icon';
import { QuoteIcon } from '@/assets/icons/quote-icon';
import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandLoading,
} from '@/components/ui/command';
import { LinkText } from '@/components/ui/link-text';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SelectTriggerButton } from '@/components/ui/select-trigger-button';
import Spinner from '@/components/ui/spinner';
import { TabsHeader } from '@/components/ui/tabs-header';
import Text from '@/components/ui/text';
import type { UseControlledStateProps } from '@/lib/hooks/use-controlled-state';
import { useControlledState } from '@/lib/hooks/use-controlled-state';
import { useLocale } from '@/lib/hooks/use-locale';
import { GlobalSearchTab, SearchEntity, SearchEntityTab } from '@/server/types/search-types';
import { SrCustomerStatus } from '@plato/data/starrocks/types';
import { Slot } from '@radix-ui/react-slot';
import type { PropsWithChildren } from 'react';
import { useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { useDebounce } from 'react-use';
import { useSearchEntityData, type SearchEntityItem } from '../../hooks/use-search-entity';

type EntitySearchContentProps = {
    entities: SearchEntity[];
    inputPlaceholder?: string;
    onItemSelected?: (item: SearchEntityItem) => void;
    search?: UseControlledStateProps<string>['state'];
    setSearch?: UseControlledStateProps<string>['setState'];
    initialTab?: GlobalSearchTab;
};

const ENTITY_ICONS = {
    [SearchEntity.Customer]: CustomerIcon,
    [SearchEntity.SalesOrder]: OrderIcon,
    [SearchEntity.SalesQuote]: QuoteIcon,
    [SearchEntity.Product]: ProductIcon,
} as const;

const NON_ACTIVE_STATUS_COLOR: Record<string, 'blue-600' | 'orange-600' | 'red-600'> = {
    [SrCustomerStatus.NEW]: 'blue-600',
    [SrCustomerStatus.AT_RISK]: 'orange-600',
    [SrCustomerStatus.INACTIVE]: 'red-600',
};

export const EntitySearchContent = ({
    entities,
    onItemSelected,
    inputPlaceholder,
    initialTab,
    ...props
}: EntitySearchContentProps) => {
    const { t } = useLocale();

    const nonActiveStatusLabels: Record<string, string> = {
        [SrCustomerStatus.NEW]: t('status_new', { ns: 'common' }),
        [SrCustomerStatus.AT_RISK]: t('status_at_risk', { ns: 'common' }),
        [SrCustomerStatus.INACTIVE]: t('status_inactive', { ns: 'common' }),
    };

    const [search, setSearch] = useControlledState({
        state: props.search,
        setState: props.setSearch,
    });
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const listRef = useRef<HTMLDivElement>(null);

    const [selectedTabKey, setSelectedTabKey] = useState<GlobalSearchTab>(() => {
        if (initialTab) {
            return initialTab;
        }
        return entities.length == 1 && entities[0] ? SearchEntityTab[entities[0]] : GlobalSearchTab.All;
    });

    const tabs =
        entities.length > 1
            ? [GlobalSearchTab.All, ...entities.map((entity) => SearchEntityTab[entity])]
            : [];

    const [, cancel] = useDebounce(
        () => {
            setDebouncedSearch(search);

            if (true) {
                requestAnimationFrame(() => {
                    listRef.current?.scrollTo({ top: 0 });
                });
            }
        },
        250,
        [search],
    );

    const { data, isLoading, isFetched } = useSearchEntityData({
        entities,
        search: debouncedSearch,
        selectedTabKey,
    });

    const handleSearchChange = (value: string) => {
        setSearch(value);
        cancel();
    };

    return (
        <>
            <CommandInput
                placeholder={inputPlaceholder || t('top_navbar__search-placeholder')}
                value={search}
                onValueChange={handleSearchChange}
                className="border-0 pb-0"
            />
            <TabsHeader<GlobalSearchTab>
                className="px-4"
                selectedTabKey={selectedTabKey}
                onTabChange={function (tabId: GlobalSearchTab): void {
                    setSelectedTabKey(tabId);
                }}
                tabs={tabs}
                getTabLabel={(tabId: string) => {
                    return t(`search__label__${tabId}`, { ns: 'common' });
                }}
            />
            <CommandList ref={listRef} className="w-full">
                {isFetched && <CommandEmpty>{t('nothing_found')}</CommandEmpty>}
                {isLoading && (
                    <CommandLoading>
                        <Spinner size={10} />
                    </CommandLoading>
                )}

                {Array.from(data.entries())
                    .filter((value) => value[1].length)
                    .map(([entity, items]) => (
                        <CommandGroup
                            key={entity}
                            heading={
                                selectedTabKey === GlobalSearchTab.All
                                    ? t(`search__label__${SearchEntityTab[entity]}`)
                                    : undefined
                            }
                        >
                            {items.map((item) => {
                                const { id, name, subName, extra } = item;
                                const Icon = ENTITY_ICONS[entity as SearchEntity];

                                return (
                                    <CommandItem
                                        key={id}
                                        value={id}
                                        onSelect={() => {
                                            onItemSelected?.(item);
                                        }}
                                    >
                                        <div className="flex flex-1 gap-2">
                                            <Icon className="flex h-4! w-4! shrink-0" />
                                            <div className="flex flex-col text-left flex-1">
                                                <Text leading="tight">{name}</Text>
                                                {(subName ||
                                                    (entity === SearchEntity.Customer &&
                                                        item.status &&
                                                        item.status !== SrCustomerStatus.ACTIVE)) && (
                                                    <Text leading="tight" color="neutral-500">
                                                        {subName}
                                                        {entity === SearchEntity.Customer &&
                                                            item.status &&
                                                            item.status !== SrCustomerStatus.ACTIVE && (
                                                                <>
                                                                    {subName && ' · '}
                                                                    <Text
                                                                        leading="tight"
                                                                        color={
                                                                            NON_ACTIVE_STATUS_COLOR[
                                                                                item.status
                                                                            ]
                                                                        }
                                                                    >
                                                                        {nonActiveStatusLabels[item.status]}
                                                                    </Text>
                                                                </>
                                                            )}
                                                    </Text>
                                                )}
                                            </div>
                                            {extra && (
                                                <Text
                                                    color="neutral-600"
                                                    leading="tight"
                                                    className="max-w-60 truncate"
                                                >
                                                    {extra}
                                                </Text>
                                            )}
                                        </div>
                                    </CommandItem>
                                );
                            })}

                            {selectedTabKey === GlobalSearchTab.All && items.length >= 6 && (
                                <CommandItem onSelect={() => setSelectedTabKey(SearchEntityTab[entity])}>
                                    <LinkText id="show-more-text" className="flex" text={t('show_more')} />
                                </CommandItem>
                            )}
                        </CommandGroup>
                    ))}
            </CommandList>
        </>
    );
};

export type EntitySearchComboboxProps = {
    entities: SearchEntity[];
    className?: string;
    setOpen?: UseControlledStateProps<boolean>['setState'];
    open?: UseControlledStateProps<boolean>['state'];
    inputPlaceholder?: string;
    onValueChange?: (value: SearchEntityItem[]) => void;
    defaultValue?: Omit<SearchEntityItem, 'original'>[];
    multiple?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    variant?: 'default' | 'inline';
    error?: boolean;
} & PropsWithChildren;

export const EntitySearchCombobox = ({
    entities,
    className,
    children,
    inputPlaceholder,
    onValueChange,
    defaultValue,
    multiple = false,
    isLoading,
    disabled,
    variant = 'default',
    error = false,
    ...props
}: EntitySearchComboboxProps) => {
    const [open, setOpen] = useControlledState({
        state: props.open,
        setState: props.setOpen,
    });
    const [search, setSearch] = useState('');

    const [selectedItems, setSelectedItems] = useState<SearchEntityItem[]>([]);

    const ref = useRef<HTMLDivElement>(null);

    const itemsToRender = (selectedItems.length ? selectedItems : defaultValue) || [];

    const triggerContent = children || (
        <SelectTriggerButton variant={variant} isLoading={isLoading} disabled={disabled} error={error}>
            {itemsToRender.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                    {itemsToRender.map((item) => (
                        <Text key={item.id} className="text-left whitespace-normal">
                            {[SearchEntity.SalesOrder, SearchEntity.SalesQuote].includes(item.entity)
                                ? item.name
                                : `${item.id} ${item.name}`}
                        </Text>
                    ))}
                </div>
            ) : (
                <Text color="neutral-600" className="text-left">
                    {inputPlaceholder || 'Select an item'}
                </Text>
            )}
        </SelectTriggerButton>
    );

    const handleItemSelected = (item: SearchEntityItem) => {
        if (!multiple) {
            setOpen(false);
        }

        const _items = ((prevItems) => {
            if (!multiple) {
                // If not multiple, clear previous items and add the new one
                return [item];
            }

            const existingItemIndex = prevItems.findIndex((prev) => prev.id === item.id);
            if (existingItemIndex > -1) {
                // If the item already exists, remove it
                return prevItems.filter((_, index) => index !== existingItemIndex);
            } else {
                // Otherwise, add the new item
                return [...prevItems, item];
            }
        })(selectedItems);

        setSelectedItems(_items);
        onValueChange?.(_items);
    };

    return (
        <div ref={ref} className={className}>
            {isDesktop ? (
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
                    <PopoverContent
                        className="p-0"
                        side="bottom"
                        align="start"
                        style={{
                            minWidth: 'var(--radix-popover-trigger-width)',
                            width: 'var(--radix-popover-trigger-width)',
                        }}
                    >
                        <Command shouldFilter={false}>
                            <EntitySearchContent
                                entities={entities}
                                onItemSelected={handleItemSelected}
                                search={search}
                                setSearch={setSearch}
                                inputPlaceholder={inputPlaceholder}
                            />
                        </Command>
                    </PopoverContent>
                </Popover>
            ) : (
                <>
                    <Slot
                        onClick={() => {
                            setOpen(!open);
                        }}
                    >
                        {triggerContent}
                    </Slot>
                    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
                        <EntitySearchContent
                            entities={entities}
                            onItemSelected={handleItemSelected}
                            search={search}
                            setSearch={setSearch}
                            inputPlaceholder={inputPlaceholder}
                        />
                    </CommandDialog>
                </>
            )}
        </div>
    );
};

EntitySearchCombobox.EntitySearchContent = EntitySearchContent;
